import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getAccountById } from '../services/firestoreService';
import BackButton from '../components/BackButton';
import BBALoader from '../components/BBALoader';
import PortalBackground from '../components/PortalBackground';
import ClientLeadHistoryTab, { LEAD_HISTORY_GLASS, leadHistoryStyles } from '../components/crm/ClientLeadHistoryTab';

/**
 * /crm/client/:clientId/lead-history — the read-only lead-era record for a client, on its own
 * page (linked from the client's Documents tab) rather than a tab inside ClientOverview.
 */
export default function ClientLeadHistory() {
  const { clientId } = useParams();
  const [account, setAccount] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAccountById(clientId);
        if (!cancelled) setAccount(data || null);
      } catch (e) {
        console.error('ClientLeadHistory: load', e);
        if (!cancelled) setError(e.message || 'Could not load this account.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const companyName = account?.company || account?.companyName || '';

  return (
    <div className="min-h-screen relative p-4 md:p-6 lg:p-8">
      <PortalBackground />
      <style>{leadHistoryStyles}</style>
      <div className="relative w-full">
        <header className="lh-rise grid grid-cols-[1fr_auto_1fr] items-center gap-3 mb-6">
          <BackButton to={`/crm/client/${clientId}`} className="justify-self-start" aria-label="Back to client" />
          <h1 className="text-center text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            {companyName ? <span>{companyName}</span> : null}
            {companyName ? <span className="mx-2 font-light text-gray-300 dark:text-gray-600">|</span> : null}
            <span className="font-semibold text-gray-500 dark:text-gray-400">📜 Lead History</span>
          </h1>
          <span
            className="justify-self-end hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium text-gray-600 dark:text-gray-300 bg-white/70 dark:bg-gray-800/70 border border-gray-200/80 dark:border-white/10"
            title="Everything this account recorded while it was a lead, frozen when it became a client."
          >
            🔒 Read-only
          </span>
        </header>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3 text-gray-600 dark:text-gray-300">
            <BBALoader size={44} />
            <span className="text-sm">Loading lead history…</span>
          </div>
        ) : error ? (
          <div className={`${LEAD_HISTORY_GLASS} rounded-2xl p-6 text-center text-red-700 dark:text-red-300`}>⚠️ {error}</div>
        ) : !account ? (
          <div className={`${LEAD_HISTORY_GLASS} rounded-2xl p-6 text-center text-gray-600 dark:text-gray-300`}>🤷 Account not found.</div>
        ) : (
          <ClientLeadHistoryTab account={account} />
        )}
      </div>
    </div>
  );
}
